import React, { useCallback, useMemo, useRef, useState } from 'react';

import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { Toast } from 'primereact/toast';

import { TaskTemplate } from '../../constants';
import { selectedDate, TreeNode } from '../../types/interfaces';
import { Task } from '../../types/interfaces';
import TablePageHeader from '../../ui/shared/TablePageHeader';
import { TagsComponent } from '../../ui/TagsComponent';
import { hasChildren, PickerTexts } from '../../utils';

import { ColumnConfig } from './COLUMN_CONFIG';
import ColumnVisibilityToggle from './ColumnVisibilityToggle';
import SearchFilterToggler from './SearchFilterToggler';
import TaskMobileFiltersDialog from './TaskMobileFiltersDialog';

interface TableHeaderProps {
  handleFilterChange: (filter: string, value: string) => void;
  selectedDepartmentsFilter: TreeNode[];
  treeDropDownValue: TreeNode[];
  handleSelectionChange: (selection: TreeNode[]) => void;
  handleDateFilterChange: (selection: selectedDate) => void;
  datepickerDate?: selectedDate;
  handleDeleteTagItem: (taskId: string | number) => void;
  exportHistory: () => void;
  exportTasks: () => void;
  importTasksXLS: (e: React.ChangeEvent<HTMLInputElement>) => void;
  exportTemplate: () => void;
  openMap: () => void;
  exportWorks: () => void;
  customExportWorks: () => void;
  isDepartmentsLoaded: boolean;
  setSelectedTask: React.Dispatch<React.SetStateAction<Task>>;
  toast: React.MutableRefObject<Toast | null>;
  isSelectedIdExist: boolean;
  onOpenCard: (task: Task) => void;
  selectedTask: Task;
  setTaskToDelete: React.Dispatch<React.SetStateAction<string | number | null>>;
  setDeleteDialogVisible: React.Dispatch<React.SetStateAction<boolean>>;
  visibleColumns: ColumnConfig[];
  setVisibleColumns: (columns: ColumnConfig[]) => void;
  columnsConfig: ColumnConfig[];
  hasHorizontalScroll: boolean;
  setSearchFilter: React.Dispatch<React.SetStateAction<string>>;
  openDepartmentAssignmentDialog: boolean;
  setOpenDepartmentAssignmentDialog: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  isMobile: boolean;
  setIsMobile: React.Dispatch<React.SetStateAction<boolean>>;
  setEmployeeDistributionDialogVisible: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  searchFilter: string;
}

const TasksTableHeader: React.FC<TableHeaderProps> = ({
  handleFilterChange,
  selectedDepartmentsFilter,
  isMobile,
  setIsMobile,
  treeDropDownValue,
  handleSelectionChange,
  handleDateFilterChange,
  datepickerDate,
  handleDeleteTagItem,
  exportHistory,
  exportTasks,
  importTasksXLS,
  exportTemplate,
  openMap,
  exportWorks,
  customExportWorks,
  isDepartmentsLoaded,
  setSelectedTask,
  toast,
  isSelectedIdExist,
  onOpenCard,
  selectedTask,
  setTaskToDelete,
  setDeleteDialogVisible,
  columnsConfig,
  visibleColumns,
  setVisibleColumns,
  hasHorizontalScroll,
  setSearchFilter,
  searchFilter,
  openDepartmentAssignmentDialog,
  setOpenDepartmentAssignmentDialog,
  setEmployeeDistributionDialogVisible
}) => {
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const filteredSelection = useMemo(
    () =>
      (selectedDepartmentsFilter as TreeNode[]).filter(
        (node) => !hasChildren(node, treeDropDownValue)
      ),
    [selectedDepartmentsFilter, treeDropDownValue]
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const menu = useRef<Menu>(null);
  const dropdownValueLenght = selectedDepartmentsFilter.length;

  const treeDropdowns = useMemo(
    () => [
      {
        customWidth: isMobile ? '100%' : '270px',
        selected: selectedDepartmentsFilter,
        value: treeDropDownValue,
        treeDropdownPlaceholder: PickerTexts.departments(dropdownValueLenght),
        onSelectionChange: handleSelectionChange,
        isLoading: !isDepartmentsLoaded,
        loadingText: 'Участки загружаются...',
        key: 'departments_dropdown',
        isRed: selectedDepartmentsFilter.length === 0,
        mode: 'tree' as const
      }
    ],
    [
      isMobile,
      selectedDepartmentsFilter,
      treeDropDownValue,
      dropdownValueLenght,
      handleSelectionChange,
      isDepartmentsLoaded
    ]
  );

  const handleTreeSelectionChange = useCallback(
    (department: TreeNode | TreeNode[]) => {
      const selection = Array.isArray(department) ? department : [department];
      handleSelectionChange(selection);
    },
    [handleSelectionChange]
  );

  const onAdd = useCallback(() => {
    setSelectedTask(TaskTemplate);
    window.open('/newTask', '_blank');
  }, [setSelectedTask]);

  const onEdit = useCallback(() => {
    if (toast.current && !isSelectedIdExist) {
      toast.current.show({
        severity: 'warn',
        summary: 'Ошибка',
        detail: 'Выберите наряд для редактирования',
        life: 3000
      });
    }
    if (isSelectedIdExist) onOpenCard(selectedTask);
  }, [isSelectedIdExist, onOpenCard, selectedTask, toast]);

  const onDelete = useCallback(() => {
    if (isSelectedIdExist) {
      setTaskToDelete(selectedTask.id);
      setDeleteDialogVisible(true);
    }
    if (toast.current && !isSelectedIdExist) {
      toast.current.show({
        severity: 'warn',
        summary: 'Ошибка',
        detail: 'Выберите наряд для удаления',
        life: 3000
      });
    }
  }, [
    isSelectedIdExist,
    selectedTask?.id,
    setDeleteDialogVisible,
    setTaskToDelete,
    toast
  ]);

  const exportItems = [
    {
      label: 'Экспорт нарядов',
      icon: 'pi pi-download',
      command: exportTasks
    },
    {
      label: 'Экспорт работ',
      icon: 'pi pi-download',
      command: exportWorks
    },
    {
      label: 'Экспорт истории',
      icon: 'pi pi-download',
      command: exportHistory
    },
    {
      label: 'Настраиваемый отчет',
      icon: 'pi pi-download',
      command: customExportWorks
    }
  ];

  const mobileExportItems = [
    {
      label: 'Экспорт нарядов',
      icon: 'pi pi-download',
      command: exportTasks
    },
    {
      label: 'Экспорт работ',
      icon: 'pi pi-download',
      command: exportWorks
    },
    {
      label: 'Экспорт истории',
      icon: 'pi pi-download',
      command: exportHistory
    }
  ];

  function setDatePickerTooltip() {
    switch (searchFilter) {
      case 'creationDateBetween':
        return 'Фильтрация по дате регистрации наряда';
      case 'plannedStartDateBetween':
        return 'Фильтрация по планируемой дате начала работ';
      case 'actualStartDateBetween':
        return 'Фильтрация по фактической дате начала работ';
      case 'plannedEndDateBetween':
        return 'Фильтрация по планируемой дате завершения работ';
      case 'actualEndDateBetween':
        return 'Фильтрация по фактической дате завершения работ';
      default:
        return '';
    }
  }

  const desktopButtonsLeft = useMemo(
    () => [
      <SearchFilterToggler
        key={`search-filters-${hasHorizontalScroll}`}
        setSearchFilter={setSearchFilter}
      />,
      <ColumnVisibilityToggle
        key={`column-visibility-${hasHorizontalScroll}`}
        columns={columnsConfig}
        visibleColumns={visibleColumns}
        onApply={(columns) => setVisibleColumns(columns)}
        hasHorizontalScroll={hasHorizontalScroll}
      />,
      <div key={`tasks-map-wrapper-${hasHorizontalScroll}`}>
        <Button
          key={`tasks-map-btn-${hasHorizontalScroll}`}
          className="tasks-map-btn__roles"
          type="button"
          severity="secondary"
          icon="pi pi-map"
          rounded
          label="Карта"
          tooltip="Карта"
          tooltipOptions={{ position: hasHorizontalScroll ? 'left' : 'top' }}
          onClick={openMap}
        />
      </div>,
      <div key={`auto-assignment-wrapper-${hasHorizontalScroll}`}>
        <Button
          key={`autoAssignment-map-btn-${hasHorizontalScroll}`}
          type="button"
          severity="secondary"
          icon="pi pi-cog"
          rounded
          className="task-card-transitions-btns__roles"
          tooltip="Автоматическое назначение нарядов на участки"
          tooltipOptions={{ position: hasHorizontalScroll ? 'left' : 'top' }}
          onClick={() =>
            setOpenDepartmentAssignmentDialog(!openDepartmentAssignmentDialog)
          }
        />
      </div>,
      <div key={`employee-distribution-wrapper-${hasHorizontalScroll}`}>
        <Button
          key={`employee-distribution-btn-${hasHorizontalScroll}`}
          type="button"
          severity="secondary"
          icon="pi pi-users"
          rounded
          className="task-card-transitions-btns__roles"
          tooltip="Автоматическое назначение нарядов на сотрудников"
          tooltipOptions={{ position: hasHorizontalScroll ? 'left' : 'top' }}
          onClick={() => setEmployeeDistributionDialogVisible(true)}
        />
      </div>
    ],
    [
      hasHorizontalScroll,
      setSearchFilter,
      columnsConfig,
      visibleColumns,
      setVisibleColumns,
      openMap,
      setOpenDepartmentAssignmentDialog,
      openDepartmentAssignmentDialog,
      setEmployeeDistributionDialogVisible
    ]
  );

  const mobileButtonsLeft = useMemo(
    () => [
      <div key={`tasks-map-wrapper-${hasHorizontalScroll}`}>
        <Button
          key="mobile-filters-toggle"
          type="button"
          icon="pi pi-sliders-h"
          rounded
          severity="secondary"
          onClick={() => setIsMobileFiltersOpen(true)}
          tooltipOptions={{ position: 'top' }}
        />
      </div>
    ],
    []
  );

  const mobileButtonsRight = useMemo(
    () => [
      <div key={`tasks-map-wrapper-${hasHorizontalScroll}`}>
        <Button
          key={`tasks-map-btn-${hasHorizontalScroll}`}
          className="tasks-map-btn__roles"
          type="button"
          severity="secondary"
          icon="pi pi-map"
          rounded
          label="Карта"
          tooltipOptions={{ position: hasHorizontalScroll ? 'left' : 'top' }}
          onClick={openMap}
        />
      </div>,
      <Button
        key="export-button"
        type="button"
        icon="pi pi-download"
        label="Отчеты"
        className="p-button-rounded p-button-secondary tasks-export-btn__roles"
        onClick={(e) => menu.current?.toggle(e)}
      />,
      <Menu key="export-menu" model={mobileExportItems} popup ref={menu} />
    ],
    []
  );

  return (
    <>
      <TablePageHeader
        isMobile={isMobile}
        tableName="tasks"
        onGlobalSearchChange={(newValue) =>
          handleFilterChange('global', newValue)
        }
        onDatePickerChange={handleDateFilterChange}
        datepickerDate={datepickerDate!}
        datepicker={isMobile ? false : true}
        treeDropdowns={isMobile ? undefined : treeDropdowns}
        onAdd={onAdd}
        onEdit={onEdit}
        onDelete={onDelete}
        hasHorizontalScroll={hasHorizontalScroll}
        datepickerTooltip={{
          from: setDatePickerTooltip(),
          until: setDatePickerTooltip()
        }}
        customButtonsLeft={isMobile ? mobileButtonsLeft : desktopButtonsLeft}
        customButtonsRight={
          isMobile
            ? mobileButtonsRight
            : [
                <input
                  key="input"
                  type="file"
                  accept="csv"
                  onChange={importTasksXLS}
                  style={{ visibility: 'hidden', width: '20px' }}
                  ref={fileInputRef}
                />,
                <Button
                  key="export-template-button"
                  className="tasks-exportTemplate-btn__roles"
                  type="button"
                  severity="secondary"
                  icon="pi pi-file-excel"
                  rounded
                  onClick={exportTemplate}
                  tooltip="Выгрузка актуального шаблона импорта"
                  tooltipOptions={{ position: 'top' }}
                />,
                <Button
                  key="import-button"
                  className="tasks-import-btn__roles"
                  type="button"
                  severity="secondary"
                  icon="pi pi-file-import"
                  rounded
                  onClick={() => {
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                    fileInputRef.current?.click();
                  }}
                  label="Импорт"
                />,
                <Button
                  key="export-button"
                  type="button"
                  icon="pi pi-download"
                  label="Отчеты"
                  className="p-button-rounded p-button-secondary tasks-export-btn__roles"
                  onClick={(e) => menu.current?.toggle(e)}
                />,
                <Menu key="export-menu" model={exportItems} popup ref={menu} />
              ]
        }
        additionalLine={
          isMobile ? null : selectedDepartmentsFilter.length > 0 ? (
            <div
              style={{ width: '100%', maxHeight: '150px', overflow: 'auto' }}
              id="tags-scroll"
            >
              <TagsComponent
                initialTagsItems={filteredSelection}
                onDelete={handleDeleteTagItem}
              />
            </div>
          ) : null
        }
      />
      {isMobile && (
        <TaskMobileFiltersDialog
          visible={isMobileFiltersOpen}
          onHide={() => setIsMobileFiltersOpen(false)}
          searchFilter={searchFilter}
          setSearchFilter={setSearchFilter}
          handleFilterChange={handleFilterChange}
          datepickerDate={datepickerDate!}
          handleDateFilterChange={handleDateFilterChange}
          hasHorizontalScroll={hasHorizontalScroll}
          treeDropDownValue={treeDropDownValue}
          selectedDepartmentsFilter={selectedDepartmentsFilter}
          handleTreeSelectionChange={handleTreeSelectionChange}
          openMap={openMap}
          setOpenDepartmentAssignmentDialog={setOpenDepartmentAssignmentDialog}
          openDepartmentAssignmentDialog={openDepartmentAssignmentDialog}
          setEmployeeDistributionDialogVisible={
            setEmployeeDistributionDialogVisible
          }
          exportTemplate={exportTemplate}
          fileInputRef={fileInputRef}
          menu={menu}
        />
      )}
    </>
  );
};

export default TasksTableHeader;
